import { observer } from "mobx-react-lite"
import { Link } from "react-router-dom"

import type { Sort } from "@/domains/table-processor"
import type { ManualClPositionStore } from "@/domains/thick-v1/positions"
import { Button } from "@/components/button"
import { ShowWhenTrue } from "@/components/conditionals"
import { Pagination } from "@/components/pagination"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableHeaderRow, TableRow } from "@/components/table"
import { TextTokenValue } from "@/components/text-token-value"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { cn, dollarsPrettified } from "@/domains/library"
import { SortIcon } from "@/domains/liquidity-page/sort-icon"
import { CL_PRODUCT_COPY } from "@/domains/manual-cl-page/cl-product-copy"
import { paginate } from "@/domains/table-processor"
import { AprBadge } from "@/domains/thick-v1/components/apr-badge"
import { IndexerPendingIndicator } from "@/domains/thick-v1/components/indexer-pending-indicator"
import { DisplayTokens } from "@/domains/tokens/components/display-tokens"

import { ClaimFeesButton } from "./claim-manual-cl-fees-button"

/**
 * Manual CL "My Positions" table — rebuilt against flagship column set.
 *
 * Columns (left → right):
 *   1. Pair (sticky) — pool tokens, fee tier, in/out-of-range badge using
 *      `position.isInRange` (keyed off `pool.currentTick`).
 *   2. NFP ID — renders the real on-chain NFP token id once indexer
 *      Proposal 4 populates `Position.nfpmTokenIds`. Before that it shows
 *      a yellow-triangle `IndexerPendingIndicator(proposal=P4)` and the
 *      short synthetic handle in muted text.
 *   3. Range — formatted tickLower/tickUpper price range.
 *   4. Position value — current USD value (sum of underlying token values at
 *      current prices).
 *   5. Earned fees — lifetime fees in USD, with a tooltip breaking down the
 *      per-token amounts and a footnote noting the reconstruction method
 *      (deposits + collected − withdrawn).
 *   6. ROI % — lifetime earned fees ÷ lifetime deposited capital.
 *   7. Age — days since the indexer first saw liquidity for this tick range.
 *   8. Claimable fees — live on-chain pending fees from the simulated
 *      `collect`. Kept because analytics can lag behind chain head.
 *   9. Claim fees button, 10. Manage action.
 *
 * All values are computed observables on `ManualClPositionStore` — no React
 * state, no `useMemo`, no data-fetching hooks.
 */
export const ManualClMyPositionsTable = observer(
  ({
    sortBy,
    setSortBy,
    currentPageNumber,
    pageSize,
    setPageSize,
    setCurrentPageNumber,
    isCompact,
    search,
  }: {
    sortBy: Sort
    setSortBy: (v: Sort) => void
    currentPageNumber: number
    setCurrentPageNumber: (v: number) => void
    pageSize: number
    setPageSize: (v: number) => void
    isCompact: boolean
    search: string
  }) => {
    const scaleApp = useScaleApplication()

    const filtered = filterBySearch(scaleApp.ManualClV1.Positions.activePositions, search)
    const sorted = sortPositions(filtered, sortBy)

    const paginated = paginate({
      currentPageNumber,
      pageSize,
      numberOfRows: sorted.length,
    })

    const rows = sorted.slice(paginated.indexes.firstItemIndexOnPage, paginated.indexes.lastItemIndexOnPage)
    const hasTableRows = sorted.length > 0

    const discovery = scaleApp.ManualClV1.Positions.nfpmDiscovery

    return (
      <TooltipProvider>
        {discovery?.truncated ? (
          <div
            role="status"
            className="border-amber-400/30 bg-amber-400/10 mb-3 flex flex-col gap-1 rounded-md border px-3 py-2 text-xs text-t200"
            data-testid="nfpm-discovery-truncation-notice"
          >
            <span className="font-medium text-t100">Some positions are hidden</span>
            <span>
              This wallet holds {discovery.totalOwned} NFPM NFTs; Scale.Farm scans the most recent {discovery.scanned}{" "}
              to keep RPC usage bounded. Older positions remain on-chain and will render here once indexer Proposal 4
              ships the full `Position.nfpmTokenIds` backfill.
            </span>
          </div>
        ) : null}
        <Table>
          <TableHead>
            <TableHeaderRow>
              <TableHeader
                className="sticky left-0 z-10 bg-p400 pl-4"
                onClick={() => toggleSort(setSortBy, sortBy, "displayName", "string")}
              >
                Pair
                <DisplaySortIcon sortBy={sortBy} column="displayName" />
              </TableHeader>
              <TableHeader
                classNameInner="justify-center"
                onClick={() => toggleSort(setSortBy, sortBy, "nfpmTokenId", "number")}
              >
                NFP ID
                <DisplaySortIcon sortBy={sortBy} column="nfpmTokenId" />
              </TableHeader>
              <TableHeader className="text-right" classNameInner="justify-end" isSortDisabled>
                Range
              </TableHeader>
              <TableHeader
                className="text-right"
                classNameInner="justify-end"
                onClick={() => toggleSort(setSortBy, sortBy, "positionValueUsd", "number")}
              >
                Position value
                <DisplaySortIcon sortBy={sortBy} column="positionValueUsd" />
              </TableHeader>
              <TableHeader
                className="text-right"
                classNameInner="justify-end"
                onClick={() => toggleSort(setSortBy, sortBy, "earnedFeesUsd", "number")}
              >
                Earned fees
                <DisplaySortIcon sortBy={sortBy} column="earnedFeesUsd" />
              </TableHeader>
              <TableHeader
                className="text-right"
                classNameInner="justify-end"
                onClick={() => toggleSort(setSortBy, sortBy, "roiPct", "number")}
              >
                ROI
                <DisplaySortIcon sortBy={sortBy} column="roiPct" />
              </TableHeader>
              <TableHeader
                className="text-right"
                classNameInner="justify-end"
                onClick={() => toggleSort(setSortBy, sortBy, "realisedAprPct", "number")}
              >
                APR
                <DisplaySortIcon sortBy={sortBy} column="realisedAprPct" />
              </TableHeader>
              <TableHeader
                className="text-right"
                classNameInner="justify-end"
                onClick={() => toggleSort(setSortBy, sortBy, "age", "number")}
              >
                Age
                <DisplaySortIcon sortBy={sortBy} column="age" />
              </TableHeader>
              <TableHeader
                className="text-right"
                classNameInner="justify-end"
                onClick={() => toggleSort(setSortBy, sortBy, "claimableFeesUsd", "number")}
              >
                Claimable fees
                <DisplaySortIcon sortBy={sortBy} column="claimableFeesUsd" />
              </TableHeader>
              <TableHeader className="w-fit" classNameInner="justify-center" isSortDisabled>
                Claim fees
              </TableHeader>
              <TableHeader className="w-fit" classNameInner="justify-center" isSortDisabled>
                Actions
              </TableHeader>
            </TableHeaderRow>
          </TableHead>
          <TableBody>
            <ShowWhenTrue when={!hasTableRows}>
              <TableRow>
                <TableCell colSpan={11} className="py-6 text-center">
                  {scaleApp.ManualClV1.Positions.isHydrated ? "No positions found" : "Loading positions..."}
                </TableCell>
              </TableRow>
            </ShowWhenTrue>
            <ShowWhenTrue when={hasTableRows}>
              {rows.map((position, index) => (
                <ManualClMyPositionsTableRow
                  key={position.positionId}
                  position={position}
                  isCompact={isCompact}
                  isFirstItem={index === 0}
                />
              ))}
            </ShowWhenTrue>
          </TableBody>
        </Table>

        <Pagination
          paginated={paginated}
          onChangeRowsPerPage={(rowsPerPage) => {
            setPageSize(rowsPerPage)
            setCurrentPageNumber(1)
          }}
          onClickPreviousPage={(previousPageNumber) => setCurrentPageNumber(previousPageNumber)}
          onClickNextPage={(nextPageNumber) => setCurrentPageNumber(nextPageNumber)}
        />
      </TooltipProvider>
    )
  },
)

const ManualClMyPositionsTableRow = observer(
  ({
    position,
    isCompact,
    isFirstItem,
  }: {
    position: ManualClPositionStore
    isCompact: boolean
    isFirstItem: boolean
  }) => {
    const pool = position.pool
    const token0 = pool.token0
    const token1 = pool.token1

    const depositedToken0 = Number(token0.formatUnits(position.underlyingAmount0))
    const depositedToken1 = Number(token1.formatUnits(position.underlyingAmount1))

    const feeTier = pool.feeTier / 1e4

    const claimableFeesToken0 =
      position.pendingFees0 === undefined ? 0 : Number(token0.formatUnits(position.pendingFees0))
    const claimableFeesToken1 =
      position.pendingFees1 === undefined ? 0 : Number(token1.formatUnits(position.pendingFees1))

    const manageHref = `/liquidity/cl/v1/${pool.address.raw}/manage/${position.tokenId.toString()}`

    return (
      <TableRow className={cn(!isFirstItem && "border-t border-t-p400-light")}>
        <TableCell
          className={cn(
            "sticky left-0 z-10 bg-p400 pl-4",
            "after:pointer-events-none after:absolute after:bottom-0 after:right-[-10px] after:top-0 after:w-[10px] after:bg-gradient-to-r after:from-white/5 after:to-transparent",
            "max-lg:after:opacity-100 lg:[&:has(+:not(:hover))]:after:opacity-0",
          )}
        >
          <div className={cn("flex flex-col items-start gap-2 xs:flex-row xs:items-center")}>
            <DisplayTokens tokens={[token0, token1]} tokenSize={30} />
            <div className="text-left">
              <div className="whitespace-nowrap text-t100">{pool.displayName}</div>
              <p className="flex flex-row items-center gap-1 text-xs text-t200">
                {CL_PRODUCT_COPY.v1.shortName} · {feeTier}%
                <span
                  className={cn(
                    "ml-1 rounded px-1.5 py-0.5 text-[10px] font-medium",
                    position.isInRange
                      ? "bg-primary-400/20 text-primary-400"
                      : "bg-error-background/15 text-error-text",
                  )}
                >
                  {position.isInRange ? "In range" : "Out of range"}
                </span>
              </p>
            </div>
          </div>
        </TableCell>
        <TableCell className="text-center">
          <NfpIdCell position={position} />
        </TableCell>
        <TableCell className="text-right">
          <RangeCell position={position} />
        </TableCell>
        <TableCell className="text-right">
          <p className="text-t100">{dollarsPrettified(position.positionValueUsd)}</p>
          <ShowWhenTrue when={!isCompact}>
            <TextTokenValue value={depositedToken0} symbol={token0.symbol} />
            <TextTokenValue value={depositedToken1} symbol={token1.symbol} />
          </ShowWhenTrue>
        </TableCell>
        <TableCell className="text-right">
          <EarnedFeesCell position={position} />
        </TableCell>
        <TableCell className="text-right">
          <RoiCell position={position} />
        </TableCell>
        <TableCell className="text-right">
          <AprCell position={position} />
        </TableCell>
        <TableCell className="text-right">
          <AgeCell timestamp={position.createdAtTimestamp} />
        </TableCell>
        <TableCell className="text-right">
          <p className="text-t100">{dollarsPrettified(position.claimableFeesUsd)}</p>
          <ShowWhenTrue when={!isCompact}>
            <PerTokenFeeBreakdown amount={claimableFeesToken0} symbol={token0.symbol} usd={position.pendingFees0Usd} />
            <PerTokenFeeBreakdown amount={claimableFeesToken1} symbol={token1.symbol} usd={position.pendingFees1Usd} />
          </ShowWhenTrue>
        </TableCell>
        <TableCell className="text-center">
          <ClaimFeesButton position={position} disabled={position.claimableFeesUsd === 0} />
        </TableCell>
        <TableCell className="text-center">
          {/*
           * The Manage page itself is navigable as soon as we know the
           * `positionId` (derived from the indexer row). Individual action
           * tabs (increase / decrease / collect / close) need the real
           * NFPM `tokenId`; that resolves on-chain via the orchestrator's
           * discovery query and usually lands in under a second, so the
           * link below is always safe to click.
           */}
          <Button variant="neutral" asChild size="md" className="mx-auto w-fit">
            <Link to={manageHref}>Manage</Link>
          </Button>
        </TableCell>
      </TableRow>
    )
  },
)

const NfpIdCell = observer(({ position }: { position: ManualClPositionStore }) => {
  if (position.nfpmTokenIdPending) {
    return (
      <div className="flex flex-col items-center gap-0.5">
        <div className="inline-flex items-center gap-1.5 text-t200">
          <span className="text-sm">#pending</span>
          <IndexerPendingIndicator proposal="P4" size="xs" />
        </div>
        <span className="text-[10px] text-t300">ref #{shortHandle(position.tokenId)}</span>
      </div>
    )
  }
  return <span className="text-t100">#{position.nfpmTokenId!.toString()}</span>
})

const RangeCell = observer(({ position }: { position: ManualClPositionStore }) => {
  const pool = position.pool
  const t0 = pool.token0.symbol
  const t1 = pool.token1.symbol
  return (
    <div className="flex flex-col items-end">
      <span className="text-sm text-t100">
        {position.priceLower} — {position.priceUpper}
      </span>
      <span className="text-[11px] text-t300">
        {t1} per 1 {t0}
      </span>
    </div>
  )
})

const EarnedFeesCell = observer(({ position }: { position: ManualClPositionStore }) => {
  const pool = position.pool
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="inline-flex cursor-default flex-col items-end">
          <span className="text-t100">{dollarsPrettified(position.earnedFeesUsd)}</span>
          <span className="text-[11px] text-t300">lifetime</span>
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="mb-1 text-t100">Fees earned</div>
        <TextTokenValue value={position.earnedFeesToken0} symbol={pool.token0.symbol} />
        <TextTokenValue value={position.earnedFeesToken1} symbol={pool.token1.symbol} />
        <div className="mt-1 text-[11px] leading-snug text-t300">
          Reconstructed from indexed deposits, withdrawals and collections. Uniswap v3 does not separate fees from
          principal on-chain, so this is an estimate — it ignores any manual transfer of fees out of the NFPM.
        </div>
      </TooltipContent>
    </Tooltip>
  )
})

/**
 * Accrued APR cell — stacks two semantically distinct badges so
 * the forward-looking "current rate" is readable at a glance instead of
 * buried in muted body text next to the main number. The top row shows
 * `realisedApr.formattedPct` ("what this position has accrued so far") as
 * a green `realised` pill; the bottom row shows `currentRate.formattedPct`
 * ("what it would earn at today's volume") as a dashed `estimated` pill.
 * Each row is preceded by a tiny uppercase label so the two APR numbers
 * can never be confused. The current-rate row is omitted when the position
 * is out of range — the in/out-of-range pill on the pair cell already
 * communicates that, and `0.00%` adds noise here.
 */
const AprCell = observer(({ position }: { position: ManualClPositionStore }) => {
  const display = position.bestAprDisplay
  if (display.status !== "ready") {
    return <span className="text-xs text-t300">{display.label}</span>
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <AprRow label={display.label} value={display.formattedPct} variant={display.variant} />
      {display.formattedDailyUsd ? <span className="text-[10px] text-t300">{display.formattedDailyUsd}</span> : null}
    </div>
  )
})

const AprRow = ({ label, value, variant }: { label: string; value: string; variant: "realised" | "estimated" }) => (
  <div className="flex items-center gap-1.5">
    <span className="text-[9px] uppercase leading-tight tracking-wider text-t300">{label}</span>
    <AprBadge value={value} variant={variant} size="sm" />
  </div>
)

const RoiCell = observer(({ position }: { position: ManualClPositionStore }) => {
  if (position.roiPct === undefined) {
    return <span className="text-t300">—</span>
  }
  const positive = position.roiPct >= 0
  return (
    <span className={positive ? "text-primary-400" : "text-error-text"}>
      {positive ? "+" : ""}
      {position.roiPct.toFixed(2)}%
    </span>
  )
})

/**
 * Dense per-token fee row — shows `0.0123 WETH` on the leading line and the
 * matching USD value in muted text right below it. `usd === undefined`
 * renders a dash so "stats RPC still pending" reads differently from "zero."
 */
const PerTokenFeeBreakdown = ({ amount, symbol, usd }: { amount: number; symbol: string; usd: number | undefined }) => (
  <div className="flex flex-col items-end">
    <TextTokenValue value={amount} symbol={symbol} />
    <span className="text-[10px] leading-tight text-t300">{usd === undefined ? "—" : dollarsPrettified(usd)}</span>
  </div>
)

const AgeCell = ({ timestamp }: { timestamp: number }) => {
  if (!timestamp || timestamp <= 0) return <span className="text-t300">—</span>
  const now = Math.floor(Date.now() / 1000)
  const secs = Math.max(0, now - timestamp)
  const days = Math.floor(secs / 86_400)
  const hours = Math.floor((secs % 86_400) / 3_600)
  const label = days >= 1 ? `${days}d ${hours}h` : hours >= 1 ? `${hours}h` : `${Math.floor(secs / 60)}m`
  return <span className="text-t100">{label}</span>
}

const DisplaySortIcon = ({ sortBy, column }: { sortBy: Sort; column: string }) => {
  if (sortBy.key !== column) return null
  return <SortIcon direction={sortBy.direction} />
}

function toggleSort(setSortBy: (v: Sort) => void, current: Sort, key: string, type: "string" | "number"): void {
  setSortBy({
    key,
    type,
    direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
  })
}

/**
 * Short, human-readable rendering of the synthetic `tokenId` the orchestrator
 * assigns while the real NFP id is still pending — keeps rows visually
 * distinct in the NFP ID column without pretending a 19-digit synthetic
 * number is a real NFT identifier.
 */
function shortHandle(tokenId: bigint): string {
  const hex = tokenId.toString(16).padStart(8, "0")
  return hex.slice(-6).toUpperCase()
}

/**
 * Applies the table search string against the pool's display name or either
 * token symbol. Keeps search behaviour identical to the legacy processor.
 */
function filterBySearch(positions: ReadonlyArray<ManualClPositionStore>, search: string): ManualClPositionStore[] {
  const trimmed = search.trim().toLowerCase()
  if (trimmed.length === 0) return [...positions]
  return positions.filter((p) => {
    const t0 = p.pool.token0.symbol.toLowerCase()
    const t1 = p.pool.token1.symbol.toLowerCase()
    const name = p.pool.displayName.toLowerCase()
    return t0.includes(trimmed) || t1.includes(trimmed) || name.includes(trimmed)
  })
}

function sortPositions(positions: ReadonlyArray<ManualClPositionStore>, sortBy: Sort): ManualClPositionStore[] {
  const multiplier = sortBy.direction === "asc" ? 1 : -1
  const compare = (a: ManualClPositionStore, b: ManualClPositionStore): number => {
    const av = pickSortValue(a, sortBy.key)
    const bv = pickSortValue(b, sortBy.key)
    if (sortBy.type === "number") {
      return (Number(av) - Number(bv)) * multiplier
    }
    return String(av).localeCompare(String(bv)) * multiplier
  }
  return [...positions].sort(compare)
}

function pickSortValue(position: ManualClPositionStore, key: string): string | number {
  switch (key) {
    case "displayName":
      return position.pool.displayName
    case "nfpmTokenId":
      return position.nfpmTokenId === undefined ? -1 : Number(position.nfpmTokenId)
    case "positionValueUsd":
      return position.positionValueUsd
    case "earnedFeesUsd":
      return position.earnedFeesUsd
    case "roiPct":
      return position.roiPct ?? -Infinity
    case "realisedAprPct":
      return position.realisedApr.aprPct ?? -Infinity
    case "age":
      return position.createdAtTimestamp
    case "claimableFeesUsd":
      return position.claimableFeesUsd
    default:
      return 0
  }
}
